/**
 * The editor's form view of a run definition. It edits a parsed copy
 * (`DefinitionFields`) and hands every change back whole; the editor sends
 * it through the backend's render, which rewrites the YAML and drops its
 * comments (ruling 6), as the note under the form says.
 *
 * The name is fixed when editing an existing definition: it is the file
 * name and the `sanctum-def/<name>` branch.
 *
 * `options` (the editor's pickers, `editor-options`) turns the failing jobs,
 * agent and model into pickers of what this project has, and gives the scan
 * report's suggestions and the budget's real bounds. Without them the form
 * still works, on typed values.
 */

import { useId, type ReactElement, type ReactNode } from 'react';
import { BRANCH_CATEGORIES, type DefinitionFields } from '../utils/definitionForm';
import { matchesJob, patternList } from '../utils/jobPattern';
import type { BranchCategory } from '../types/projectPanel.types';
import type { CiJob, EditorOptions } from '../schemas/editorOptions.schemas';
import { AgentModelFields } from './AgentModelFields';
import { BudgetFields } from './BudgetFields';
import { JobPicker } from './JobPicker';

interface DefinitionFormProps {
  fields: DefinitionFields;
  nameLocked: boolean;
  onChange: (fields: DefinitionFields) => void;
  options?: EditorOptions | undefined;
}

const BRANCH_LABELS: Record<BranchCategory, string> = {
  default: 'Default branch',
  protected: 'Protected branches',
  feature: 'Feature branches',
  merge_request: 'Merge request pipelines',
};

const REPORT_HINT =
  'A Grype JSON report the failing job saves as an artifact. Sanctum reads it from the job and gives the agent its findings as a table; without one the agent gets the job log.';

/** The JSON artifacts of the jobs the patterns match: the report path's suggestions. */
function reportSuggestions(patterns: string, jobs: CiJob[]): string[] {
  const list = patternList(patterns);
  const matched = jobs.filter((j) => list.some((p) => matchesJob(p, j.name)));
  return [...new Set(matched.flatMap((j) => j.reports))];
}

type TextField = Exclude<keyof DefinitionFields, 'branches' | 'agent'>;

/** The hint's id, which the field's control names in `aria-describedby`. */
const hintId = (id: string): string => `${id}-hint`;

function Field(props: { id: string; label: string; hint?: ReactNode; children: ReactNode }): ReactElement {
  return (
    <div className="form-group def-form__field">
      <label htmlFor={props.id}>{props.label}</label>
      {props.children}
      {props.hint !== undefined && <span id={hintId(props.id)} className="def-form__hint">{props.hint}</span>}
    </div>
  );
}

/** `aria-describedby` for a control whose Field shows a hint. */
const describedBy = (id: string, hint: boolean): { 'aria-describedby'?: string } =>
  hint ? { 'aria-describedby': hintId(id) } : {};

function Branches(props: { value: BranchCategory[]; onChange: (value: BranchCategory[]) => void }): ReactElement {
  const toggle = (branch: BranchCategory): void => {
    const on = props.value.includes(branch);
    props.onChange(BRANCH_CATEGORIES.filter((b) => (b === branch ? !on : props.value.includes(b))));
  };
  return (
    <fieldset className="def-form__branches">
      <legend>Branches</legend>
      {BRANCH_CATEGORIES.map((branch) => (
        <label key={branch} className="def-form__check">
          <input type="checkbox" checked={props.value.includes(branch)} onChange={() => { toggle(branch); }} />
          {BRANCH_LABELS[branch]}
        </label>
      ))}
    </fieldset>
  );
}

export function DefinitionForm({ fields, nameLocked, onChange, options }: DefinitionFormProps): ReactElement {
  const id = useId();
  const set = <K extends keyof DefinitionFields>(key: K, value: DefinitionFields[K]): void => {
    onChange({ ...fields, [key]: value });
  };
  const text = (key: TextField): { id: string; value: string; onChange: (e: { target: { value: string } }) => void } => ({
    id: `${id}-${key}`,
    value: fields[key],
    onChange: (e) => { set(key, e.target.value); },
  });
  return (
    <div className="def-form">
      <Field id={`${id}-name`} label="Name" hint={nameLocked ? 'The file and branch name; fixed once created.' : 'Lowercase letters, digits and dashes.'}>
        <input type="text" {...text('name')} {...describedBy(`${id}-name`, true)} readOnly={nameLocked} autoComplete="off" spellCheck={false} />
      </Field>
      <JobPicker
        id={`${id}-patterns`}
        patterns={fields.patterns}
        jobs={options === undefined || options.jobs_error !== null ? null : options.jobs}
        note={options?.jobs_error ?? (options?.jobs_truncated === true ? 'Only the first jobs are listed' : null)}
        onChange={(value) => { set('patterns', value); }}
      />
      <Branches value={fields.branches} onChange={(value) => { set('branches', value); }} />
      <div className="def-form__row">
        <Field id={`${id}-priority`} label="Priority">
          <input type="number" min={0} max={100} placeholder="0" {...text('priority')} />
        </Field>
        <AgentModelFields
          id={id}
          agent={fields.agent}
          model={fields.model}
          options={options}
          onAgent={(value) => { set('agent', value); }}
          onModel={(value) => { set('model', value); }}
        />
      </div>
      <Field id={`${id}-instructions`} label="Instructions">
        <textarea className="def-form__textarea" rows={5} {...text('instructions')} />
      </Field>
      <Field id={`${id}-reportPath`} label="Scan report for the agent (optional)" hint={REPORT_HINT}>
        <input
          type="text"
          placeholder="none: the agent gets the job log"
          autoComplete="off"
          spellCheck={false}
          list={`${id}-reports`}
          {...text('reportPath')}
          {...describedBy(`${id}-reportPath`, true)}
        />
        <datalist id={`${id}-reports`}>
          {reportSuggestions(fields.patterns, options?.jobs ?? []).map((p) => <option key={p} value={p} />)}
        </datalist>
      </Field>
      <BudgetFields
        id={id}
        minutes={text('budgetMinutes')}
        tokens={text('budgetTokens')}
        budget={options?.budget}
      />
      <p className="def-form__note">Editing in the form rewrites the YAML and drops comments.</p>
    </div>
  );
}
